import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { Button, Radio, Space } from "antd";
import { runInAction } from "mobx";

import { userStore } from "../stores/userStore";
import { WebGisPage } from "./WebGisPage";

import type { WebGisData } from "../api/webgis";

const ALLOWED_HOSTS = ["localhost", "127.0.0.1", "my.nextgis.dev"];
const PREVIEW_WEBGIS: WebGisData = {
  subdomain_name: "preview",
  state: "suspended",
  state_transition: null,
  language: "en",
  url: "https://preview.nextgis.com",
  url_oauth: "https://preview.nextgis.com/oauth?na=next",
};

export const TestWebGisPage = () => {
  const { t } = useTranslation();
  const [scenario, setScenario] = useState<"success" | "waiting" | "error">("success");
  const [preview, setPreview] = useState(0);
  const [ready, setReady] = useState(false);
  const savedState = useRef({
    webgis: userStore.webgis,
    webgisLoaded: userStore.webgisLoaded,
  });

  useEffect(() => {
    if (!ALLOWED_HOSTS.includes(window.location.hostname)) return;
    const originalState = savedState.current;
    userStore.setWebgis(PREVIEW_WEBGIS);
    const readyTimer = window.setTimeout(() => setReady(true), 0);
    return () => {
      window.clearTimeout(readyTimer);
      runInAction(() => {
        userStore.webgis = originalState.webgis;
        userStore.webgisLoaded = originalState.webgisLoaded;
      });
    };
  }, []);

  if (!ALLOWED_HOSTS.includes(window.location.hostname)) {
    return <Navigate to="/" replace />;
  }

  const resetPreview = () => {
    userStore.setWebgis(PREVIEW_WEBGIS);
    setPreview((value) => value + 1);
  };

  const mockUnsuspend = async () => {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 700));
    if (scenario === "error") throw new Error("Simulated unsuspension error");
    return {};
  };

  const mockWaitForUnsuspension = async (): Promise<WebGisData> => {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 1000));
    return {
      ...PREVIEW_WEBGIS,
      state: scenario === "waiting" ? "suspended" : "started",
      state_transition: scenario === "waiting" ? "wait_for_unsuspend" : null,
    };
  };

  return (
    <>
      <div className="page">
        <p>{t("webgis.unsuspend.previewDescription")}</p>
        <Space wrap>
          <Link to="/test">{t("app.goBack")}</Link>
          <Radio.Group
            value={scenario}
            onChange={(event) => {
              setScenario(event.target.value);
              resetPreview();
            }}
            optionType="button"
            options={[
              { label: t("webgis.unsuspend.previewSuccess"), value: "success" },
              { label: t("webgis.unsuspend.previewWaiting"), value: "waiting" },
              { label: t("webgis.unsuspend.previewError"), value: "error" },
            ]}
          />
          <Button onClick={resetPreview}>{t("mailing.previewReset")}</Button>
        </Space>
      </div>
      {ready && (
        <WebGisPage
          key={preview}
          onUnsuspend={mockUnsuspend}
          onWaitForUnsuspension={mockWaitForUnsuspension}
        />
      )}
    </>
  );
};
